import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import type { FinalSong } from '../../interfaces/final-song';
import { ModalType } from '../../interfaces/modal-types.enum';
import { usePlayerStore } from '../../stores/player-store';
import { useGlobalStore } from '../../stores/global-store';
import { useUserStore } from '../../stores/user-store';
import { useDeviceStore } from '../../stores/device-store';
import { useToastStore } from '../../stores/toast-store';
import { useModalStore } from '../../stores/modal-store';
import { sendEventToServer } from '../../api/http-req';
import { logger } from '../logging/logger';
import { popupWindow } from '../utils/window.helper';
import { useIsEllipsis } from '../hooks/useIsEllipsis';
import { safeDestroyYoutubePlayer } from './player-teardown';
import { playPlaylistSong } from '../playback/play-from-playlist';
import AppImage from './AppImage';
import Tooltip from './Tooltip';
import SocialSharing from './SocialSharing';
import './Player.scss';

// Port of src/app/shared/components/player/ (mypart-player).
//
// Angular → React mapping:
// - selectedSongSignal (player store) drives the `.show` class on the root;
//   the local selectedSong signal (set 150ms debounced) drives the content.
// - constructor effect #1 (debounced selectedSong handling: engagement event,
//   SH_SONG_PLAY, player/audio init) → useEffect on the store's selectedSong
//   with the same 150ms setTimeout debounce + distinctUntilChanged by id.
// - constructor effect #2 (first/last song ids from songs()) → useEffect.
// - constructor effect #3 (auto-start when playAllSongs turns on) → useEffect.
// - ngAfterViewInit (YT api readiness probe) → mount effect.
// - Plain class fields (player, isApiReady, debounceTimer, counters, …) → refs.
// - The Angular next/prev navigation walked result-item DOM children
//   (firstChild.firstChild.children[1].firstChild). The React DOM marks the
//   clickable inline players with [data-play-target] (see YouTubeIframe /
//   Mp3Player), so the equivalent here is querySelector('[data-play-target]')
//   inside the [data-result-item] container — same click target, same effect.
// - Angular's add/removeEventListener used `.bind(this)` so the removal never
//   actually detached (new function identity each call); here a stable
//   callback is used so detaching works.
// - `showPlayer` and the private handleIphone() were never referenced by the
//   Angular template / code path — not ported.

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

function isIphone(): boolean {
  return /iPhone|iPad|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

function isSafari(): boolean {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

function formatTime(seconds: number): string {
  if (!seconds || isNaN(seconds)) return '0:00';

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

function getCurrentResultItemContainer(youtube_id: string | undefined): HTMLElement | null {
  return document.querySelector(`[data-result-item='${youtube_id}']`) as HTMLElement | null;
}

function getPreviousResultItemContainer(currentContainer: HTMLElement | null): HTMLElement | null {
  let previousContainer = currentContainer?.previousElementSibling;
  while (previousContainer && !previousContainer.matches('[data-result-item]')) {
    previousContainer = previousContainer.previousElementSibling;
  }

  return previousContainer as HTMLElement | null;
}

function getNextResultItemContainer(currentContainer: HTMLElement | null): HTMLElement | null {
  let nextContainer = currentContainer?.nextElementSibling;
  while (nextContainer && !nextContainer.matches('[data-result-item]')) {
    nextContainer = nextContainer.nextElementSibling;
  }

  return nextContainer as HTMLElement | null;
}

function getPlayTargetContainer(container: HTMLElement | null): HTMLElement | null {
  return (container?.querySelector('[data-play-target]') as HTMLElement | null) ?? null;
}

function getFirstResultItem(): HTMLElement | null {
  return document.querySelector('[data-result-item]') as HTMLElement | null;
}

export default function Player() {
  const storeSelectedSong = usePlayerStore(s => s.selectedSong);
  const songPlayedFromPlaylist = usePlayerStore(s => s.songPlayedFromPlaylist);
  const songs = useGlobalStore(s => s.songs);
  const playlistSongs = useGlobalStore(s => s.playlistSongs);
  const playAllSongs = useGlobalStore(s => s.playAllSongs);
  const isMobileDevice = useDeviceStore(s => s.isMobileDevice);

  const [selectedSong, setSelectedSongState] = useState<FinalSong | undefined>(undefined);
  const [firstSongId, setFirstSongId] = useState('');
  const [lastSongId, setLastSongId] = useState('');
  const [isSongPlaying, setIsSongPlaying] = useState(false);
  const [showControls, setShowControlsState] = useState(false);

  // Progress tracking signals
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [duration, setDuration] = useState('0:00');
  const [bufferProgress, setBufferProgress] = useState(0);
  const [previewTime, setPreviewTime] = useState('');
  const [previewPosition, setPreviewPosition] = useState(0);

  const isThisIphone = useRef(isIphone()).current;

  // Plain (non-signal) Angular fields → refs.
  const selectedSongRef = useRef<FinalSong | undefined>(undefined);
  const showControlsRef = useRef(false);
  const playerRef = useRef<any>(null);
  // Offscreen host that holds the YouTube iframe when a song is played FROM THE
  // PLAYLIST (that song may not be in the results DOM, so there is no inline
  // result-row placeholder to bind to). See startVideoInit / the render.
  const playlistVideoHostRef = useRef<HTMLDivElement>(null);
  const audioElementRef = useRef<HTMLAudioElement | undefined>(undefined);
  // One-shot guard so we log "audio playing" once per song, on the first frame
  // that actually advances (currentTime > 0) — proof of real playback, vs the
  // old optimistic log at init time. Reset whenever a new audio element loads.
  const audioStartLoggedRef = useRef(false);
  const isApiReadyRef = useRef(false);
  const initializationInProgressRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The playback source (playlist host vs inline result tile) the current song
  // was last initialised for. Part of the distinctUntilChanged key so the SAME
  // song can be re-initialised when its source changes.
  const lastFromPlaylistRef = useRef(false);
  const songsWereAddedToPlaylistRef = useRef(false);
  const songsToAddToPlaylistRef = useRef<FinalSong[]>([]);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const setSelectedSong = (song: FinalSong | undefined) => {
    selectedSongRef.current = song;
    setSelectedSongState(song);
  };

  const setShowControls = (value: boolean) => {
    showControlsRef.current = value;
    setShowControlsState(value);
  };

  const toast = useToastStore.getState();

  const updateProgress = useCallback(() => {
    try {
      const player = playerRef.current;
      if (player && typeof player.getCurrentTime === 'function') {
        const current = player.getCurrentTime() || 0;
        const total = player.getDuration() || 0;
        const loaded = player.getVideoLoadedFraction() || 0;

        setProgress((current / total) * 100);
        setCurrentTime(formatTime(current));
        setDuration(formatTime(total));
        setBufferProgress(loaded * 100);
      } else if (audioElementRef.current) {
        const audioElement = audioElementRef.current;
        const current = audioElement.currentTime;
        const total = audioElement.duration;
        const loaded = audioElement.buffered.length
          ? (audioElement.buffered.end(audioElement.buffered.length - 1) / total) * 100
          : 0;

        setProgress((current / total) * 100);
        setCurrentTime(formatTime(current));
        setDuration(formatTime(total));
        setBufferProgress(loaded);
      }
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  }, []);

  const stopProgressTracking = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const startProgressTracking = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    progressIntervalRef.current = setInterval(() => {
      updateProgress();
    }, 1000);
  }, [updateProgress]);

  const destroyPlayer = useCallback(() => {
    // NOTE(port): detach the ref FIRST. (1) A player torn down before it
    // finished initialising can't be destroyed here (YT hasn't attached its
    // methods yet) — nulling the ref lets onPlayerReady recognise it as an
    // orphan and destroy it once ready, instead of letting it autoplay
    // headless. (2) safeDestroyYoutubePlayer guards each call, so teardown
    // never throws past destroy() the way the old inline version did (which
    // left showControls=false wedged and the player orphaned — the reported
    // stuck/unclickable mini-player).
    const player = playerRef.current;
    playerRef.current = null;
    safeDestroyYoutubePlayer(player);
    // Clear any playlist iframe/placeholder from the offscreen host (destroy()
    // removes the API's iframe but leaves our placeholder div behind).
    playlistVideoHostRef.current?.replaceChildren();
    // Stop the outgoing audio too. An inline result MP3 pauses itself via its
    // own Mp3Player effect when another song starts, but a playlist MP3 plays
    // through a Player-owned `new Audio()` that is detached from the DOM — nothing
    // else stops it, so without this it keeps playing under the next song (the
    // "plays both, doesn't stop the previous" bug). destroyPlayer runs at the
    // start of every switch (effect #1), so pausing here covers every source.
    if (audioElementRef.current) {
      audioElementRef.current.pause();
    }
    setShowControls(false);
    setIsSongPlaying(false);
    stopProgressTracking();
  }, [stopProgressTracking]);

  const returnToSongLocation = () => {
    // Playlist songs have no results-list position; nothing to scroll to.
    if (usePlayerStore.getState().songPosition == null) {
      return;
    }
    setTimeout(() => {
      const results = document.getElementById('results-scroll-container')!;
      results.scrollTo({
        top: usePlayerStore.getState().songPosition!
          - (useGlobalStore.getState().navBar?.offsetHeight ?? 0) - 10,
        behavior: 'smooth'
      });
    }, 0);
  };

  const playVideo = useCallback(() => {
    if (!showControlsRef.current) {
      return;
    }
    if (playerRef.current) {
      try {
        playerRef.current.playVideo();
      } catch (err) {
        logger.warn('playback', 'youtube playVideo() threw', {
          songId: selectedSongRef.current?.id,
          message: (err as Error)?.message,
        });
      }
      return;
    }
    if (audioElementRef.current) {
      // Swallow the AbortError when a pause()/load() interrupts this play()
      // during rapid song switching (previously surfaced as unhandledrejection),
      // but record that the requested playback did not start (e.g. NotAllowedError
      // = autoplay blocked, AbortError = superseded by a newer song).
      audioElementRef.current.play().catch((err: DOMException) => {
        logger.warn('playback', 'audio play did not start', {
          songId: selectedSongRef.current?.id,
          reason: err?.name,
          message: err?.message,
        });
      });
    }
  }, []);

  const pauseVideo = useCallback(() => {
    if (!showControlsRef.current) {
      return;
    }
    if (playerRef.current) {
      playerRef.current.pauseVideo();
      return;
    }
    if (audioElementRef.current) {
      audioElementRef.current.pause();
    }
  }, []);

  // Playlist next/prev: index the playlist array by the current song's id and
  // start the neighbour via the store (the Player owns its media). Returns
  // false when there is no such neighbour (first/last song).
  const playPlaylistSongByOffset = useCallback((offset: number): boolean => {
    const arr = Array.from(useGlobalStore.getState().playlistSongs.values());
    const idx = arr.findIndex(s => s.id === selectedSongRef.current?.id);
    if (idx < 0) {
      return false;
    }
    const target = arr[idx + offset];
    if (!target) {
      return false;
    }
    playPlaylistSong(target);
    return true;
  }, []);

  const playPreviousSong = () => {
    if (!showControlsRef.current) {
      return;
    }
    if (usePlayerStore.getState().songPlayedFromPlaylist) {
      playPlaylistSongByOffset(-1);
      return;
    }
    const currentItemResult = getCurrentResultItemContainer(selectedSongRef.current?.youtube_id);
    const previousItemResult = getPreviousResultItemContainer(currentItemResult);
    const youtubeContainer = getPlayTargetContainer(previousItemResult);
    if (youtubeContainer) {
      youtubeContainer.click();
      returnToSongLocation();
    }
  };

  const playNextSong = useCallback(() => {
    if (!showControlsRef.current) {
      return;
    }
    if (usePlayerStore.getState().songPlayedFromPlaylist) {
      playPlaylistSongByOffset(1);
      return;
    }
    const currentItemResult = getCurrentResultItemContainer(selectedSongRef.current?.youtube_id);
    const nextItemResult = getNextResultItemContainer(currentItemResult);
    const youtubeContainer = getPlayTargetContainer(nextItemResult);
    if (youtubeContainer) {
      youtubeContainer.click();
      returnToSongLocation();
    }
  }, [playPlaylistSongByOffset]);

  const onPlayerStateChange = useCallback((event: { data: number }) => {
    switch (event.data) {
      case -1: // (unstarted)
        // On Safari/iOS, we might need to attempt playback again
        if ((isThisIphone || isSafari()) &&
          (useGlobalStore.getState().playAllSongs || usePlayerStore.getState().songPlayedFromPlaylist)) {
          setTimeout(() => {
            playVideo();
          }, 1000);
        }
        break;
      case 0: // (ended)
        setIsSongPlaying(false);
        logger.info('playback', 'youtube ended', {
          songId: selectedSongRef.current?.id,
          youtube_id: selectedSongRef.current?.youtube_id,
          title: selectedSongRef.current?.title,
          artist: selectedSongRef.current?.artist,
        });
        if (useGlobalStore.getState().playAllSongs || usePlayerStore.getState().songPlayedFromPlaylist) {
          // Add delay for Safari/iOS to handle state changes
          setTimeout(() => {
            playNextSong();
          }, 500);
        }
        break;
      case 1: // (playing)
        setIsSongPlaying(true);
        logger.info('playback', 'youtube playing', {
          songId: selectedSongRef.current?.id,
          youtube_id: selectedSongRef.current?.youtube_id,
          title: selectedSongRef.current?.title,
          artist: selectedSongRef.current?.artist,
        });
        break;
      case 2: // (paused)
        setIsSongPlaying(false);
        break;
      case 3: // (buffering)
        break;
      case 5: // (video cued)
        // On Safari/iOS, we might need to attempt playback
        if ((isThisIphone || isSafari()) &&
          (useGlobalStore.getState().playAllSongs || usePlayerStore.getState().songPlayedFromPlaylist)) {
          setTimeout(() => {
            playVideo();
          }, 1000);
        }
        break;
    }
  }, [isThisIphone, playVideo, playNextSong]);

  const onError = useCallback((event: any) => {
    const errorCode = event?.data;
    let errorMessage = 'An error occurred while playing the video.';

    // Handle specific YouTube player error codes
    switch (errorCode) {
      case 2:
        errorMessage = 'Invalid video ID or parameter.';
        break;
      case 5:
        errorMessage = 'HTML5 player error.';
        break;
      case 100:
        errorMessage = 'Video not found or removed.';
        break;
      case 101:
      case 150:
        errorMessage = 'Video playback not allowed.';
        break;
    }

    // Log error and notify user
    sendEventToServer('INVALID_YOUTUBE_LINK', {
      songId: selectedSongRef.current?.id,
      errorCode,
      errorMessage
    });
    logger.error('playback', `youtube error ${errorCode}`, {
      songId: selectedSongRef.current?.id,
      youtube_id: selectedSongRef.current?.youtube_id,
      title: selectedSongRef.current?.title,
      errorCode,
      errorMessage,
    });

    toast.error(errorMessage);
    destroyPlayer();

    // Auto-play next song if playAllSongs is enabled
    if (useGlobalStore.getState().playAllSongs) {
      // Add delay for Safari/iOS to handle state changes
      setTimeout(() => {
        playNextSong();
      }, 500);
    }
  }, [destroyPlayer, playNextSong, toast]);

  const onPlayerReady = useCallback((event: any) => {
    // NOTE(port): a player torn down before it finished initialising (rapid
    // song switching) still fires onReady. Its YT methods only exist now, so
    // destroyPlayer() couldn't stop it earlier. If it is no longer the current
    // player it's an orphan — destroy it now rather than let it autoplay with
    // nothing controlling it (the stuck/unclickable mini-player bug).
    if (event?.target !== playerRef.current) {
      try {
        event?.target?.destroy?.();
      } catch {
        // already torn down — nothing to do
      }
      return;
    }
    // Ensure player is ready before attempting playback
    if (event?.target?.playVideo) {
      setTimeout(() => {
        playVideo();
      }, 100);
      setShowControls(true);
      setIsSongPlaying(true);
      startProgressTracking();
    } else {
      // Even if player fails to initialize, enable controls
      // so user can try to play other songs
      setShowControls(true);
      logger.error('playback', 'youtube player failed to initialize', {
        songId: selectedSongRef.current?.id,
      });
      toast.error('Player failed to initialize properly');
    }
  }, [playVideo, startProgressTracking, toast]);

  const startVideoInit = useCallback(() => {
    // Ensure we don't have race conditions with multiple init calls
    if (initializationInProgressRef.current) {
      return;
    }

    initializationInProgressRef.current = true;

    try {
      destroyPlayer();

      const YT = window.YT;
      const youtubeId = selectedSongRef.current?.youtube_id ?? '';

      // Render target: the inline result tile's placeholder when the song is on
      // screen (visible video — the common case, including playlist songs played
      // via playPlaylistSong), else the Player's offscreen host for a playlist
      // song that isn't in the current results. destroyPlayer() (run just above)
      // clears the host first, so getElementById can only resolve to a live grid
      // tile here — no stale host/grid id collision.
      let video = document.getElementById(youtubeId);
      if (!video) {
        const host = playlistVideoHostRef.current;
        if (!host) {
          throw new Error('Playlist video host not found');
        }
        host.replaceChildren();
        const placeholder = document.createElement('div');
        placeholder.id = youtubeId;
        placeholder.className = 'w-full h-full';
        host.appendChild(placeholder);
        video = placeholder;
      }

      // Configure the player. NOTE(port): `video` is the empty placeholder <div>
      // YouTubeIframe injects — so the YouTube API CREATES the iframe itself from
      // videoId + playerVars (it owns the load lifecycle, so onReady fires
      // reliably and autoplay/start actually apply). Binding to a pre-existing
      // iframe instead raced the embed's load and silently dropped onReady — the
      // locked/no-autoplay bug. `start` seeks to the song's timestamp.
      const playerVars: any = {
        'autoplay': 1,
        'playsinline': 1,
        'start': Math.floor(selectedSongRef.current?.timestamp ?? 0),
        'origin': window.location.origin
      };

      // For iOS devices, ensure playsinline is enabled
      if (isThisIphone) {
        playerVars.playsinline = 1;
        playerVars.controls = 1; // Show native controls for better iOS compatibility
      }

      playerRef.current = new YT.Player(video, {
        videoId: selectedSongRef.current?.youtube_id,
        playerVars,
        events: {
          'onReady': onPlayerReady,
          'onStateChange': onPlayerStateChange,
          'onError': onError
        }
      });
      // Watchdog: if onReady never fires (the controls would stay disabled and
      // progress frozen), surface it in the log instead of failing silently.
      const createdPlayer = playerRef.current;
      setTimeout(() => {
        if (playerRef.current === createdPlayer && !showControlsRef.current) {
          logger.error('playback', 'youtube player onReady did not fire', {
            songId: selectedSongRef.current?.id,
            youtube_id: selectedSongRef.current?.youtube_id,
          });
        }
      }, 5000);
    } catch (error) {
      toast.error('Failed to initialize video player');
      console.error('Video initialization error:', error);
    } finally {
      initializationInProgressRef.current = false;
    }
  }, [destroyPlayer, isThisIphone, onPlayerReady, onPlayerStateChange, onError, toast]);

  const updatePlayerState = useCallback(() => {
    try {
      const audioElement = audioElementRef.current;
      if (audioElement) {
        // NOTE(port): the Angular original also required currentTime > 0, but
        // at the `play` event currentTime is still 0, so the bottom bar showed
        // ▶ (play) while the inline thumbnail was already playing — the icon
        // desync reported in the field. Drive purely off paused/ended/error.
        const isPlaying = !audioElement.paused &&
          !audioElement.ended &&
          !audioElement.error;

        setIsSongPlaying(isPlaying);

        // Confirm real playback (sound actually progressing) exactly once per
        // song — distinct from the icon state above, which flips on play/pause.
        if (isPlaying && audioElement.currentTime > 0 && !audioStartLoggedRef.current) {
          audioStartLoggedRef.current = true;
          logger.info('playback', 'audio playing', {
            songId: selectedSongRef.current?.id,
            title: selectedSongRef.current?.title,
            artist: selectedSongRef.current?.artist,
            url: usePlayerStore.getState().playingAudioUrl,
          });
        }

        // Handle audio element errors
        if (audioElement.error) {
          const errorMessage = `Audio playback error: ${audioElement.error.message}`;
          logger.error('playback', 'audio playback error', {
            songId: selectedSongRef.current?.id,
            message: audioElement.error.message,
            code: audioElement.error.code,
          });
          toast.error(errorMessage);

          if (useGlobalStore.getState().playAllSongs) {
            playNextSong();
          }
        }
      }
    } catch (error) {
      console.error('Error updating player state:', error);
    }
  }, [playNextSong, toast]);

  // Auto-advance when the Player-owned audio (or a results MP3 under Play All)
  // finishes. Separate from updatePlayerState, which only mirrors play/pause
  // state and can't tell which event fired.
  const onAudioEnded = useCallback(() => {
    if (useGlobalStore.getState().playAllSongs || usePlayerStore.getState().songPlayedFromPlaylist) {
      playNextSong();
    }
  }, [playNextSong]);

  const startAudioElementListeners = useCallback(() => {
    const audioElement = audioElementRef.current;
    if (audioElement) {
      audioElement.addEventListener('play', updatePlayerState);
      audioElement.addEventListener('pause', updatePlayerState);
      audioElement.addEventListener('ended', updatePlayerState);
      audioElement.addEventListener('ended', onAudioEnded);
      audioElement.addEventListener('timeupdate', updatePlayerState);
    }
  }, [updatePlayerState, onAudioEnded]);

  const stopAudioElementListeners = useCallback(() => {
    const audioElement = audioElementRef.current;
    if (audioElement) {
      audioElement.removeEventListener('play', updatePlayerState);
      audioElement.removeEventListener('pause', updatePlayerState);
      audioElement.removeEventListener('ended', updatePlayerState);
      audioElement.removeEventListener('ended', onAudioEnded);
      audioElement.removeEventListener('timeupdate', updatePlayerState);
    }
  }, [updatePlayerState, onAudioEnded]);

  const initAudioElement = useCallback(() => {
    try {
      audioElementRef.current = usePlayerStore.getState().playingAudioElement;

      if (!audioElementRef.current) {
        throw new Error('Audio element not initialized');
      }

      audioStartLoggedRef.current = false;
      startAudioElementListeners();
      setShowControls(true);
      startProgressTracking();
      // NOTE(port): "audio playing" is logged later, on the first real
      // currentTime advance (see updatePlayerState), not here at init — init
      // does not prove the element actually started producing sound.
    } catch (error) {
      logger.error('playback', 'audio failed to initialize', {
        songId: selectedSongRef.current?.id,
        message: (error as Error)?.message,
      });
      toast.error('Failed to initialize audio player');
      console.error('Audio initialization error:', error);
    }
  }, [startAudioElementListeners, startProgressTracking, toast]);

  const initYoutubeApi = useCallback(() => {
    try {
      if (window.YT && window.YT.Player) {
        // The YouTube API is already loaded
        isApiReadyRef.current = true;
      } else {
        // Load the YouTube IFrame API if not already loaded
        if (!document.getElementById('youtube-api')) {
          const tag = document.createElement('script');
          tag.id = 'youtube-api';
          tag.src = 'https://www.youtube.com/iframe_api';
          const firstScriptTag = document.getElementsByTagName('script')[0];
          firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
        }

        // Set up the onYouTubeIframeAPIReady callback
        window.onYouTubeIframeAPIReady = () => {
          isApiReadyRef.current = true;
          // If we have a pending video initialization, start it now
          if (usePlayerStore.getState().playingIframeId) {
            startVideoInit();
          }
        };
      }
    } catch (error) {
      console.error('Error initializing YouTube API:', error);
      toast.error('Failed to initialize YouTube player');
    }
  }, [startVideoInit, toast]);

  const checkUserPlaylistEngagement = useCallback((newSong: FinalSong) => {
    const globalState = useGlobalStore.getState();
    if (!songsWereAddedToPlaylistRef.current && globalState.playlistSongs.size === 0) {
      songsToAddToPlaylistRef.current.push(newSong);

      if (songsToAddToPlaylistRef.current.length === 5) {
        songsWereAddedToPlaylistRef.current = true;
        songsToAddToPlaylistRef.current.forEach(song => {
          useGlobalStore.getState().addRemoveSongFromPlaylist(song);
          toast.info('We added the last 5 songs you played to your playlist. Export it to Spotify or keep exploring your song recommendations!', 8000);
          useGlobalStore.getState().setGlobalField('showPlaylist', true);
          if (useDeviceStore.getState().isMobileDevice) {
            setTimeout(() => {
              useGlobalStore.getState().setGlobalField('showPlaylist', false);
            }, 2000);
          }
        });
      }
    }
  }, [toast]);

  // ngAfterViewInit: probe the YouTube IFrame API readiness.
  useEffect(() => {
    if (window.YT && window.YT.Player) {
      // The YouTube API is already loaded
      isApiReadyRef.current = true;
    } else {
      // Set up the onYouTubeIframeAPIReady callback
      window.onYouTubeIframeAPIReady = () => {
        isApiReadyRef.current = true;
      };
    }
  }, []);

  // Constructor effect #1: debounced reaction to the player store's selected
  // song (engagement tracking, SH_SONG_PLAY event, player/audio bootstrap).
  useEffect(() => {
    const newSong = storeSelectedSong;

    // Clear any pending debounce timer.
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set a new debounce timer (150ms).
    debounceTimerRef.current = setTimeout(() => {
      // distinctUntilChanged keyed on (song id + playback source). The source is
      // part of the key so replaying the SAME song from a different target
      // re-initialises: e.g. a song playing from the playlist lives in the hidden
      // offscreen host, and clicking that same song in the results grid must move
      // it into the inline result tile — without this it stays in the host and the
      // tile spins forever (no player ever binds to its placeholder).
      const fromPlaylistNow = usePlayerStore.getState().songPlayedFromPlaylist;
      if (selectedSongRef.current?.id === newSong?.id && lastFromPlaylistRef.current === fromPlaylistNow) {
        return;
      }
      lastFromPlaylistRef.current = fromPlaylistNow;

      // If there is a new song, send an event to the server.
      if (newSong) {
        checkUserPlaylistEngagement(newSong);

        const user = useUserStore.getState().user;
        sendEventToServer('SH_SONG_PLAY', {
          email: user ? user.email : 'no user',
          artworkId: newSong.id,
          url: window.location.href,
          songIndex: useGlobalStore.getState().songs.findIndex(song => song.id === newSong.id)
        });

        destroyPlayer();
        setShowControls(false);
        setSelectedSong(newSong);

        if (usePlayerStore.getState().playingIframeId) {
          stopAudioElementListeners();
          if (!isApiReadyRef.current) {
            initYoutubeApi();
          }
          if (isApiReadyRef.current) {
            setTimeout(() => {
              startVideoInit();
            }, 100);
            return;
          }
        }
        if (usePlayerStore.getState().playingAudioUrl) {
          setShowControls(true);
          initAudioElement();
          // Playlist MP3: the Player owns this <audio> element, so nothing else
          // will start it (results MP3s are started by their inline Mp3Player).
          if (usePlayerStore.getState().songPlayedFromPlaylist) {
            audioElementRef.current?.play().catch((err: DOMException) => {
              logger.warn('playback', 'audio play did not start', {
                songId: selectedSongRef.current?.id,
                reason: err?.name,
                message: err?.message,
              });
            });
          }
        }
      }
    }, 150);
    // songPlayedFromPlaylist is a dep so the effect re-fires when only the source
    // changes (same song, playlist host -> inline result tile), even though
    // storeSelectedSong keeps the same reference.
  }, [storeSelectedSong, songPlayedFromPlaylist, checkUserPlaylistEngagement, destroyPlayer,
    stopAudioElementListeners, initYoutubeApi, startVideoInit, initAudioElement]);

  // Constructor effect #2: keep first/last song ids in sync with the results.
  useEffect(() => {
    if (songs.length > 0) {
      setFirstSongId(songs[0].id);
      setLastSongId(songs[songs.length - 1].id);
    }
  }, [songs]);

  const playAllSongsFromTheBeginning = useCallback(() => {
    // For iOS devices, we need user interaction first
    if (isThisIphone) {
      toast.info('On iOS devices, please tap the song you want to start with first');
      return;
    }

    // For Safari, warn about potential autoplay restrictions
    if (isSafari()) {
      toast.info('Safari may restrict automatic playback. Click any song to start playing.');
    }

    // Enable controls immediately when Play All is activated
    // This ensures buttons are enabled even before a song starts playing
    setShowControls(true);

    const currentItemResult = getFirstResultItem();
    const youtubeContainer = getPlayTargetContainer(currentItemResult);
    if (youtubeContainer) {
      youtubeContainer.click();
      returnToSongLocation();
    } else {
      // If no song is found, show error and keep controls enabled
      toast.warning('No songs available to play');
    }
  }, [isThisIphone, toast]);

  // Constructor effect #3: auto-start playback when Play All is switched on.
  useEffect(() => {
    if (playAllSongs && !selectedSong && songs.length > 0) {
      playAllSongsFromTheBeginning();
    }
  }, [playAllSongs, selectedSong, songs, playAllSongsFromTheBeginning]);

  // Expose play/pause to the playlist bar and mirror the current playing state,
  // so a playlist row can show its playing/paused icon and pause/resume the
  // active song without reaching into this component. playVideo/pauseVideo are
  // stable (useCallback []), so controls register once.
  useEffect(() => {
    usePlayerStore.getState().setControls({ play: playVideo, pause: pauseVideo });
    return () => usePlayerStore.getState().setControls(null);
  }, [playVideo, pauseVideo]);

  useEffect(() => {
    usePlayerStore.getState().setIsPlaying(isSongPlaying);
  }, [isSongPlaying]);

  // Unmount cleanup (Angular relied on component destruction).
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      stopProgressTracking();
      stopAudioElementListeners();
      destroyPlayer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openLyrics = () => {
    const song = selectedSongRef.current;
    if (song) {
      if (song.lyrics && useUserStore.getState().isUserWhiteLabel) {
        useModalStore.getState().open(ModalType.LYRICS, {
          inputs: {
            title: song.title,
            artist: song.artist,
            lyrics: song.lyrics
          }
        });
      } else {
        const query = encodeURIComponent(`${song.artist} - ${song.title} lyrics`);
        const url = `https://www.google.com/search?q=${query}`;
        popupWindow(url, 'MyPart Songhunt', 640, 480);
      }
    }
  };

  const togglePlayAllSongs = () => {
    const globalState = useGlobalStore.getState();
    if (globalState.playAllSongs) {
      globalState.setGlobalField('playAllSongs', false);
      toast.info('Play all songs is not active');
    } else {
      globalState.setGlobalField('playAllSongs', true);
      toast.info('Play all songs is active');
    }
  };

  const seekTo = (event: MouseEvent<HTMLDivElement>) => {
    if (!showControlsRef.current) {
      return;
    }

    try {
      const progressBar = event.currentTarget as HTMLElement;
      const rect = progressBar.getBoundingClientRect();
      const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
      const percentage = (x / rect.width) * 100;

      // Update visual progress immediately for better UX
      setProgress(percentage);

      if (playerRef.current && typeof playerRef.current.getDuration === 'function') {
        const total = playerRef.current.getDuration();
        const seekTime = (total * percentage) / 100;
        playerRef.current.seekTo(seekTime, true);
      } else if (audioElementRef.current) {
        const total = audioElementRef.current.duration;
        if (!isNaN(total)) {
          audioElementRef.current.currentTime = (total * percentage) / 100;
        }
      }
    } catch (error) {
      console.error('Error seeking:', error);
      toast.error('Failed to seek to position');
    }
  };

  const showPreviewTime = (event: MouseEvent<HTMLDivElement>) => {
    if (!showControlsRef.current) {
      return;
    }

    try {
      const progressBar = event.currentTarget as HTMLElement;
      const rect = progressBar.getBoundingClientRect();
      const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
      const percentage = (x / rect.width) * 100;

      let total = 0;
      if (playerRef.current && typeof playerRef.current.getDuration === 'function') {
        total = playerRef.current.getDuration();
      } else if (audioElementRef.current) {
        total = audioElementRef.current.duration;
      }

      if (total) {
        const previewSeconds = (total * percentage) / 100;
        setPreviewTime(formatTime(previewSeconds));
        setPreviewPosition(percentage);
      }
    } catch (error) {
      console.error('Error showing preview time:', error);
    }
  };

  const hidePreviewTime = () => {
    setPreviewTime('');
  };

  const { ref: titleEllipsisRef } = useIsEllipsis<HTMLSpanElement>();
  const { ref: artistEllipsisRef } = useIsEllipsis<HTMLSpanElement>();

  const controlSvgClasses = (extra = '') =>
    `${showControls ? 'cursor-pointer text-white' : 'cursor-not-allowed text-gray-300'}${extra}`;

  const song = selectedSong;

  // First/last boundaries for the prev/next disabling. When playing from the
  // playlist, they come from the playlist order rather than the results list.
  const playlistArray = songPlayedFromPlaylist ? Array.from(playlistSongs.values()) : null;
  const effectiveFirstSongId = playlistArray ? (playlistArray[0]?.id ?? '') : firstSongId;
  const effectiveLastSongId = playlistArray
    ? (playlistArray[playlistArray.length - 1]?.id ?? '')
    : lastSongId;

  return (
    <div onClick={returnToSongLocation}
         className={`player fixed bottom-0 left-0 right-0 overflow-hidden md:rounded-tr-lg md:rounded-tl-lg cursor-pointer
            md:-translate-x-1/2 md:left-1/2 md:max-w-lg shadow-2xl${storeSelectedSong ? ' show' : ''}`}>
      {/* Offscreen host for the YouTube iframe when a song is played from the
          playlist (kept visible-but-offscreen, never display:none, so autoplay
          after the user's click is honoured). */}
      <div ref={playlistVideoHostRef}
           aria-hidden="true"
           style={{ position: 'fixed', left: -9999, top: -9999, width: 320, height: 180, opacity: 0, pointerEvents: 'none' }} />
      {song && (
        <div className="flex justify-between items-center pb-3 px-4 relative h-full">
          {/* Song Info Section */}
          <div className="flex min-w-0 gap-x-4 flex-grow song-info -mt-4">
            <AppImage alt={`Cover image for the song - ${song.title}, By - ${song.artist}`} src={song.imageUrl}
                      className="flex-none bg-gray-50 cover-image"
                      height={48} width={48} />
            <div className="min-w-0 flex-auto flex flex-col justify-center">
              <span ref={titleEllipsisRef}
                    className="select-none text-white font-medium text-sm md:text-md max-[600px]:whitespace-normal ellipsis">
                {song.title}
              </span>
              <span ref={artistEllipsisRef}
                    className="select-none text-xs md:text-sm text-gray-200 opacity-90 ellipsis mt-0.5">
                {song.artist}
              </span>
            </div>
          </div>

          {/* Player Controls Section */}
          <div className="flex items-center gap-x-4 -mt-4">
            <svg onClick={(e) => { e.stopPropagation(); playPreviousSong(); }}
                 className={`w-8 h-8 ${controlSvgClasses()}${!showControls || effectiveFirstSongId === song.id ? ' disabled' : ''}${effectiveFirstSongId === song.id ? ' opacity-0' : ''}`}
                 fill="none"
                 stroke="currentColor"
                 strokeLinecap="round"
                 strokeLinejoin="round"
                 strokeWidth="2"
                 viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="m12 19-7-7 7-7"/>
              <path d="m19 19-7-7 7-7"/>
            </svg>

            {isSongPlaying ? (
              <svg onClick={(e) => { e.stopPropagation(); pauseVideo(); }}
                   className={`w-9 h-9 ${controlSvgClasses()}${!showControls ? ' disabled' : ''}`}
                   fill="none"
                   stroke="currentColor"
                   strokeLinecap="round"
                   strokeLinejoin="round"
                   strokeWidth="2"
                   viewBox="0 0 24 24"
                   xmlns="http://www.w3.org/2000/svg">
                <rect height="16" width="4" x="6" y="4"/>
                <rect height="16" width="4" x="14" y="4"/>
              </svg>
            ) : (
              <svg onClick={(e) => { e.stopPropagation(); playVideo(); }}
                   className={`w-9 h-9 ${controlSvgClasses()}${!showControls ? ' disabled' : ''}`}
                   fill="none"
                   stroke="currentColor"
                   strokeLinecap="round"
                   strokeLinejoin="round"
                   strokeWidth="2"
                   viewBox="0 0 24 24"
                   xmlns="http://www.w3.org/2000/svg">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
            )}

            <svg onClick={(e) => { e.stopPropagation(); playNextSong(); }}
                 className={`w-8 h-8 ${controlSvgClasses()}${!showControls || effectiveLastSongId === song.id ? ' disabled' : ''}${effectiveLastSongId === song.id ? ' opacity-0' : ''}`}
                 fill="none"
                 stroke="currentColor"
                 strokeLinecap="round"
                 strokeLinejoin="round"
                 strokeWidth="2"
                 viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="m5 19 7-7-7-7"/>
              <path d="m12 19 7-7-7-7"/>
            </svg>

            {!isThisIphone && (
              <Tooltip content={playAllSongs ? 'Stop Play All' : 'Play All Songs'} disabled={isMobileDevice}>
                <svg onClick={(e) => { e.stopPropagation(); togglePlayAllSongs(); }}
                     aria-hidden="true"
                     className={`w-5 h-5 text-white cursor-pointer no-effect${playAllSongs ? ' text-yellow-300' : ''}`}
                     fill="currentColor"
                     height="20"
                     preserveAspectRatio="xMidYMid meet"
                     viewBox="0 0 358.000000 293.000000"
                     xmlns="http://www.w3.org/2000/svg">
                  <g fill="currentColor"
                     stroke="none" transform="translate(0.000000,293.000000) scale(0.100000,-0.100000)">
                    <path d="M81 2834 c-45 -37 -53 -79 -49 -246 3 -164 11 -186 79 -220 32 -17
                            120 -18 1419 -18 1323 0 1387 1 1419 18 64 34 72 65 69 255 l-3 169 -37 34
                            -38 34 -1414 0 -1414 0 -31 -26z"/>
                    <path d="M85 1898 c-46 -36 -55 -73 -55 -230 0 -158 10 -193 63 -232 28 -21
                            36 -21 756 -21 678 0 729 1 754 18 51 33 61 60 65 188 6 175 -2 226 -45 266
                            l-34 33 -738 0 -738 0 -28 -22z"/>
                    {playAllSongs ? (
                      <>
                        <rect height="1826" rx="70" ry="70" width="600" x="2100" y="0"/>
                        <rect height="1826" rx="70" ry="70" width="600" x="2900" y="0"/>
                      </>
                    ) : (
                      <path d="M2185 1826 c-40 -17 -84 -63 -101 -103 -11 -26 -14 -174 -14 -792 0
                            -512 3 -769 11 -788 24 -64 109 -122 179 -123 19 0 58 11 85 25 28 13 293 181
                            590 373 614 397 598 383 603 505 3 83 -17 130 -78 178 -50 39 -1108 719 -1137
                            730 -35 13 -103 10 -138 -5z"/>
                    )}
                    <path d="M92 960 c-55 -34 -64 -71 -60 -249 5 -254 -22 -241 503 -241 437 0
                            437 0 475 63 18 28 20 51 20 190 0 176 -7 203 -63 237 -30 19 -53 20 -437 20
                            -389 0 -406 -1 -438 -20z"/>
                  </g>
                </svg>
              </Tooltip>
            )}

            <Tooltip content="Lyrics Lookup" disabled={isMobileDevice}>
              <svg onClick={(e) => { e.stopPropagation(); openLyrics(); }}
                   className="w-5 h-5 text-white cursor-pointer no-effect"
                   fill="none"
                   stroke="currentColor"
                   strokeLinecap="round"
                   strokeLinejoin="round"
                   strokeWidth="2"
                   viewBox="0 0 24 24"
                   xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <path d="M14 2v6h6"/>
                <line x1="16" x2="8" y1="13" y2="13"/>
                <line x1="16" x2="8" y1="17" y2="17"/>
                <line x1="10" x2="8" y1="9" y2="9"/>
              </svg>
            </Tooltip>

            <SocialSharing
                link={song.youtube_id}
                shareColor="text-white"
                source="song"
                stopPropagation />
          </div>

          {/* Progress Bar Section */}
          <div className="progress-container mb-2">
            {/* Time Display */}
            <div className="time-display">
              <span className="current-time">{currentTime}</span>
              <span className="duration">{duration}</span>
            </div>

            {/* Progress Bar */}
            <div onClick={seekTo}
                 onMouseLeave={hidePreviewTime}
                 onMouseMove={showPreviewTime}
                 className="progress-bar">
              {/* Buffer Progress */}
              <div style={{ width: `${bufferProgress}%` }}
                   className="buffer-progress">
              </div>
              {/* Playback Progress */}
              <div style={{ width: `${progress}%` }}
                   className="playback-progress">
                {/* Progress Handle */}
                <div className="progress-handle"></div>
              </div>
              {/* Preview Time Tooltip */}
              {previewTime && (
                <div style={{ left: `${previewPosition}%` }}
                     className="preview-tooltip">
                  {previewTime}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
